import { useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { Joyride } from "react-joyride";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { useTourState } from "@/hooks/useTourState";
import { resolveDashboardVariant } from "@/lib/dashboardVariant";
import { useDashboardTourSteps, type DashboardTourVariant } from "./useDashboardTourSteps";
import { tourOptions, tourStyles } from "./tourStyles";
import { useTourCallback } from "./useTourCallback";
import { useTranslation } from "react-i18next";

export function DashboardTour() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const auth = useAuth();
  const { enabled: tourEnabled, isLoading } = useFeatureFlag(
    "app-tour",
    FEATURE_FLAGS["app-tour"] ?? false,
  );
  const { isRunning, hasCompletedTour, completeTour, startTour } = useTourState("dashboard");

  // TES-1701: resolve which '/'-dashboard the user lands on (mirrors HomeRoute) so
  // the tour only contains steps whose anchor is actually mounted for that variant.
  // 'admin-redirect' / 'qm-admin-redirect' (TES-2101) never render a dashboard → null
  // → empty step set → no tour.
  const variant = resolveDashboardVariant({
    isSuperAdmin: auth.isSuperAdmin,
    isAdmin: auth.isAdmin,
    isQmAdmin: auth.isQmAdmin,
    isQualitaetsmanager: auth.isQualitaetsmanager,
    isTester: auth.isTester,
    isMangelbearbeiter: auth.isMangelbearbeiter,
    hasActiveTenant: !!auth.profile?.active_tenant_id,
  });
  const tourVariant: DashboardTourVariant | null =
    variant === "admin-redirect" || variant === "qm-admin-redirect" ? null : variant;
  const steps = useDashboardTourSteps(tourVariant);

  const isDashboard = pathname === "/" || pathname === "/dashboard";
  // TES-1701: variants whose dashboard mounts no `dashboard-*` anchor (super-admin,
  // admin-redirect) get an EMPTY step set from useDashboardTourSteps. Never auto-start
  // or render Joyride for them — an empty/anchor-less tour run is exactly the silent
  // skip the owner reported.
  const hasSteps = steps.length > 0;

  // Auto-start on first dashboard visit
  useEffect(() => {
    if (!isLoading && tourEnabled && hasSteps && !hasCompletedTour() && isDashboard) {
      const timer = setTimeout(() => startTour(), 2500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, tourEnabled, hasSteps, hasCompletedTour, startTour, isDashboard]);

  // TES-1659: shared Joyride onEvent — scroll the step target's real scroll
  // container (<main>) into view (joyride's window-only engine can't) + finish
  // bookkeeping. Dashboard also restores body overflow on finish.
  const restoreBodyOverflow = useCallback(() => {
    document.body.style.overflow = "";
  }, []);
  const handleCallback = useTourCallback(completeTour, restoreBodyOverflow);

  if (!tourEnabled || isLoading || !isDashboard || !hasSteps) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      continuous
      scrollToFirstStep
      onEvent={handleCallback}
      options={tourOptions}
      styles={tourStyles}
      locale={{
        back: t("tour.buttons.back"),
        close: t("tour.buttons.finish"),
        last: t("tour.buttons.finish"),
        next: t("tour.buttons.next"),
        skip: t("tour.buttons.skip"),
      }}
    />
  );
}
